import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

interface User {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  status: string;
  emailVerified: boolean;
  mfaEnabled: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function UsersPage() {
  const { t } = useTranslation('superadmin');
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'user' as 'superadmin' | 'admin' | 'user',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [pagination.page, statusFilter, roleFilter, searchQuery]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (statusFilter) params.set('status', statusFilter);
      if (roleFilter) params.set('role', roleFilter);
      if (searchQuery) params.set('search', searchQuery);

      const response = await api.get(`/api/admin/users?${params}`);
      if (response.ok) {
        const data = await response.json() as { users: User[]; pagination: typeof pagination };
        setUsers(data.users);
        setPagination(data.pagination);
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to load users');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      const response = await api.post('/api/admin/users', createForm);
      if (response.ok) {
        setShowCreateModal(false);
        setCreateForm({ email: '', password: '', displayName: '', role: 'user' });
        loadUsers();
      } else {
        const data = await response.json() as { error?: string };
        setCreateError(data.error || 'Failed to create user');
      }
    } catch (err) {
      setCreateError('Failed to connect to server');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    if (!confirm(t('users.confirmSuspend'))) return;

    try {
      const response = await api.patch(`/api/admin/users/${userId}`, {
        status: 'suspended',
      });
      if (response.ok) {
        loadUsers();
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to suspend user');
      }
    } catch (err) {
      alert('Failed to connect to server');
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      const response = await api.patch(`/api/admin/users/${userId}`, {
        status: 'active',
      });
      if (response.ok) {
        loadUsers();
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to activate user');
      }
    } catch (err) {
      alert('Failed to connect to server');
    }
  };

  const handleApproveUser = async (userId: string) => {
    if (!confirm(t('users.confirmApprove'))) return;

    try {
      const response = await api.patch(`/api/admin/users/${userId}`, {
        status: 'active',
      });
      if (response.ok) {
        loadUsers();
        alert(t('users.approveSuccess'));
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to approve user');
      }
    } catch (err) {
      alert('Failed to connect to server');
    }
  };

  const handleRejectUser = async (userId: string) => {
    if (!confirm(t('users.confirmReject'))) return;

    try {
      const response = await api.patch(`/api/admin/users/${userId}`, {
        status: 'suspended',
      });
      if (response.ok) {
        loadUsers();
        alert(t('users.rejectSuccess'));
      } else {
        const data = await response.json() as { error?: string };
        alert(data.error || 'Failed to reject user');
      }
    } catch (err) {
      alert('Failed to connect to server');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('users.createUser')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder={t('users.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('users.allStatuses')}</option>
            <option value="active">{t('users.active')}</option>
            <option value="suspended">{t('dashboard.suspended')}</option>
            <option value="pending">{t('users.pending')}</option>
          </select>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">{t('users.allRoles')}</option>
            <option value="superadmin">{t('users.superadmin')}</option>
            <option value="admin">{t('users.admin')}</option>
            <option value="user">{t('users.user')}</option>
          </select>

          <button
            onClick={loadUsers}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('users.refresh')}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.user')}
                </th>
                <th className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.role')}
                </th>
                <th className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.status')}
                </th>
                <th className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.security')}
                </th>
                <th className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.joined')}
                </th>
                <th className="px-6 py-3 ltr:text-right rtl:text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.displayName || t('users.noName')}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center ltr:space-x-2 rtl:space-x-reverse rtl:space-x-2">
                      {user.emailVerified ? (
                        <span className="text-green-600" title={t('users.emailVerified')}>✓ Email</span>
                      ) : (
                        <span className="text-gray-400" title={t('users.emailNotVerified')}>✗ Email</span>
                      )}
                      {user.mfaEnabled ? (
                        <span className="text-green-600" title={t('users.mfaEnabled')}>✓ MFA</span>
                      ) : (
                        <span className="text-gray-400" title={t('users.mfaDisabled')}>✗ MFA</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap ltr:text-right rtl:text-left text-sm">
                    <div className="flex items-center ltr:justify-end rtl:justify-start ltr:space-x-2 rtl:space-x-reverse rtl:space-x-2">
                      <Link
                        to={`/superadmin/users/${user.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {t('users.view')}
                      </Link>
                      {user.status === 'pending' ? (
                        <>
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            {t('users.approve')}
                          </button>
                          <button
                            onClick={() => handleRejectUser(user.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            {t('users.reject')}
                          </button>
                        </>
                      ) : user.status === 'active' ? (
                        <button
                          onClick={() => handleSuspendUser(user.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          {t('users.suspend')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivateUser(user.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          {t('users.activate')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              {t('users.showing')} {((pagination.page - 1) * pagination.limit) + 1} {t('users.to')}{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} {t('users.of')}{' '}
              {pagination.total} {t('users.users')}
            </div>
            <div className="flex ltr:space-x-2 rtl:space-x-reverse rtl:space-x-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('users.previous')}
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('users.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('users.createUser')}</h2>

            {createError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.email')}
                </label>
                <input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.password')}
                </label>
                <input
                  type="password"
                  required
                  minLength={12}
                  value={createForm.password}
                  onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('users.passwordHint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.displayNameOptional')}
                </label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm(f => ({ ...f, displayName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.role')}
                </label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(f => ({ ...f, role: e.target.value as 'user' | 'admin' | 'superadmin' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">{t('users.user')}</option>
                  <option value="admin">{t('users.admin')}</option>
                  <option value="superadmin">{t('users.superadmin')}</option>
                </select>
              </div>

              <div className="flex ltr:justify-end rtl:justify-start ltr:space-x-3 rtl:space-x-reverse rtl:space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('users.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createLoading ? t('users.creating') : t('users.createUser')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
